import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import CircularProgress from "@mui/material/CircularProgress";
import Drawer from "@mui/material/Drawer";
import Divider from "@mui/material/Divider";
import Grow from "@mui/material/Grow";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import LinearProgress from "@mui/material/LinearProgress";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import Stack from "@mui/material/Stack";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import Popover from "@mui/material/Popover";
import Button from "@mui/material/Button";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SkuflixListVideoIcon from "core/icons/skuflix/SkuflixListVideoIcon";
import SkuflixCrossIcon from "core/icons/skuflix/SkuflixCrossIcon";
import PlayerFooterButton from "core/components/PlayerFooterButton";
import { useTranslation } from "react-i18next";
import moment from "core/utils/moment";

function formatTime(date, timezone) {
  if (!date) {
    return "";
  }

  if (timezone) {
    return moment(date).tz(timezone).format("HH:mm");
  }

  return moment(date).format("HH:mm");
}

function getProgress(dateFrom, dateTo) {
  if (!dateFrom || !dateTo) {
    return 0;
  }

  const now = Date.now();
  const start = new Date(dateFrom).getTime();
  const end = new Date(dateTo).getTime();

  if (now < start) {
    return 0;
  }

  if (now > end) {
    return 100;
  }

  return ((now - start) / (end - start)) * 100;
}

function isCurrent(dateFrom, dateTo) {
  const now = Date.now();
  return (
    now >= new Date(dateFrom).getTime() && now < new Date(dateTo).getTime()
  );
}

function getItemSecondaryText(item, timezone) {
  const parts = [formatTime(item.dateFrom, timezone)];

  if (Array.isArray(item.categories) && item.categories.length > 0) {
    parts.push(item.categories.join(", "));
  }

  return parts.join(" · ");
}

function getItemDescriptionText(item) {
  return item.description || "";
}

function ScheduleHeader({
  title,
  hasPrevious,
  hasNext,
  loading,
  onPrevious,
  onNext,
  isMobile = false,
  onClose,
}) {
  if (isMobile) {
    return (
      <>
        <AppBar position="static" color="default" elevation={0}>
          <Toolbar>
            <IconButton
              edge="start"
              size="large"
              aria-label="close schedule"
              onClick={onClose}
            >
              <SkuflixCrossIcon />
            </IconButton>
            <Typography
              variant="h5"
              noWrap
              sx={{
                flex: 1,
                ml: 1,
              }}
            >
              {title}
            </Typography>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <IconButton
                size="large"
                aria-label="previous programmes"
                onClick={onPrevious}
                disabled={!hasPrevious || loading}
              >
                <ChevronLeftIcon />
              </IconButton>
              <IconButton
                size="large"
                aria-label="next programmes"
                onClick={onNext}
                disabled={!hasNext || loading}
              >
                <ChevronRightIcon />
              </IconButton>
            </Stack>
          </Toolbar>
        </AppBar>
        <Divider />
      </>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        px: 0.5,
        py: 0.5,
        gap: 0.5,
      }}
    >
      <IconButton
        size="small"
        aria-label="previous programmes"
        onClick={onPrevious}
        disabled={!hasPrevious || loading}
      >
        <ChevronLeftIcon fontSize="small" />
      </IconButton>

      <Typography
        variant="subtitle2"
        sx={{
          flex: 1,
          textAlign: "center",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {title}
      </Typography>

      <IconButton
        size="small"
        aria-label="next programmes"
        onClick={onNext}
        disabled={!hasNext || loading}
      >
        <ChevronRightIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}

ScheduleHeader.propTypes = {
  title: PropTypes.string.isRequired,
  hasPrevious: PropTypes.bool,
  hasNext: PropTypes.bool,
  loading: PropTypes.bool,
  onPrevious: PropTypes.func,
  onNext: PropTypes.func,
  isMobile: PropTypes.bool,
  onClose: PropTypes.func,
};

export default function ChannelSchedule({
  items = [],
  loading = false,
  hasPrevious = false,
  hasNext = false,
  onPrevious,
  onNext,
  timezone,
  variant = "footer",
}) {
  const { t } = useTranslation("core");
  const theme = useTheme();
  const isMobile = useMediaQuery("(max-width: 450px)");
  const [open, setOpen] = useState(false);
  const [infoAnchorEl, setInfoAnchorEl] = useState(null);
  const [infoText, setInfoText] = useState("");
  const [scheduleAnchorEl, setScheduleAnchorEl] = useState(null);
  const [, setTick] = useState(0);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const interval = setInterval(() => {
      setTick((value) => value + 1);
    }, 10000);

    return () => clearInterval(interval);
  }, [open]);

  if (!loading && !items.length) {
    return null;
  }

  const headerTitle = t("epgButton.title");
  const displayTimezone = timezone || null;

  const epgPanelSurfaceSx = {
    bgcolor: theme.palette.background.body,
    backgroundImage: "none",
    "&&": {
      backgroundColor: theme.palette.background.body,
    },
  };

  const epgInfoPopoverPaperSx = {
    px: 1.5,
    py: 1,
    maxWidth: isMobile ? "calc(100vw - 24px)" : 520,
    bgcolor: theme.palette.tooltip.main,
    backgroundImage: "none",
    "&&": {
      backgroundColor: theme.palette.tooltip.main,
    },
  };

  const epgInfoPopoverSlotProps = {
    root: {
      sx: {
        zIndex: theme.zIndex.tooltip,
      },
    },
    paper: {
      sx: epgInfoPopoverPaperSx,
    },
  };
  const epgSchedulePopperModifiers = [
    {
      name: "flip",
      enabled: true,
      options: {
        fallbackPlacements: ["top-end", "bottom-start", "bottom-end"],
      },
    },
    {
      name: "preventOverflow",
      enabled: true,
      options: {
        padding: 8,
        boundary: "viewport",
        altBoundary: true,
        tether: true,
      },
    },
  ];
  const epgSchedulePopperPlacement =
    variant === "outlined" ? "bottom-start" : "top-start";

  const handleOpenInfo = (event, text) => {
    event.stopPropagation();
    setInfoAnchorEl(event.currentTarget);
    setInfoText(text || "");
  };

  const handleCloseInfo = () => {
    setInfoAnchorEl(null);
    setInfoText("");
  };
  const handleClick = (event) => {
    setScheduleAnchorEl(event.currentTarget);
    setOpen((value) => !value);
  };
  const handleClose = () => {
    setScheduleAnchorEl(null);
    setOpen(false);
  };
  const button =
    variant === "outlined" ? (
      <Button
        variant="outlined"
        color="inherit"
        startIcon={<SkuflixListVideoIcon />}
        onClick={handleClick}
        sx={{ whiteSpace: "nowrap" }}
      >
        {t("epgButton.title")}
      </Button>
    ) : (
      <PlayerFooterButton
        title={open ? "" : t("epgButton.title")}
        onClick={handleClick}
      >
        <SkuflixListVideoIcon />
      </PlayerFooterButton>
    );

  const listItems = items.map((item, index) => {
    const current = isCurrent(item.dateFrom, item.dateTo);
    const progress = current ? getProgress(item.dateFrom, item.dateTo) : 0;
    const infoTextContent = getItemDescriptionText(item);
    const secondaryText = getItemSecondaryText(item, displayTimezone);

    return (
      <ListItem
        key={item.id}
        divider={index < items.length - 1}
        secondaryAction={
          infoTextContent ? (
            isMobile ? (
              <IconButton
                edge="end"
                size="small"
                onClick={(event) => handleOpenInfo(event, infoTextContent)}
              >
                <InfoOutlinedIcon fontSize="inherit" />
              </IconButton>
            ) : (
              <Tooltip title={infoTextContent} arrow>
                <IconButton edge="end" size="small">
                  <InfoOutlinedIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            )
          ) : null
        }
        sx={{
          position: "relative",
          overflow: "hidden",
          minHeight: 82,
          alignItems: "flex-start",
          backgroundColor: current
            ? theme.palette.mode === "dark"
              ? theme.palette.tooltip.main
              : "action.selected"
            : "transparent",
        }}
      >
        <ListItemText
          primary={item.title}
          secondary={
            <>
              <Typography
                variant="body2"
                component="span"
                sx={{ display: "block" }}
              >
                {secondaryText}
              </Typography>
              {infoTextContent ? (
                <Typography
                  variant="caption"
                  component="span"
                  sx={{
                    mt: 0.5,
                    color: "text.secondary",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    whiteSpace: "normal",
                  }}
                >
                  {infoTextContent}
                </Typography>
              ) : null}
            </>
          }
          primaryTypographyProps={{
            sx: {
              fontWeight: current ? 600 : 400,
            },
          }}
        />
        {current && (
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: 0,
              height: 3,
              borderRadius: 0,
            }}
          />
        )}
      </ListItem>
    );
  });

  const schedulePanelContent = (
    <Box
      sx={{
        minHeight: 260,
        maxHeight: isMobile ? "calc(100dvh - 56px)" : "60vh",
        overflowY: "auto",
      }}
    >
      {loading && !items.length ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <List disablePadding dense>
          {listItems}
        </List>
      )}
    </Box>
  );

  const scheduleHeader = (
    <ScheduleHeader
      title={headerTitle}
      hasPrevious={hasPrevious}
      hasNext={hasNext}
      loading={loading}
      onPrevious={onPrevious}
      onNext={onNext}
      isMobile={isMobile}
      onClose={handleClose}
    />
  );

  if (isMobile) {
    return (
      <>
        <Box sx={{ position: "relative" }}>{button}</Box>
        <Drawer
          anchor="bottom"
          open={open}
          onClose={handleClose}
          disableScrollLock
          PaperProps={{
            sx: {
              borderTopLeftRadius: 0,
              borderTopRightRadius: 0,
              width: "100%",
              height: "100dvh",
              maxHeight: "100dvh",
              ...epgPanelSurfaceSx,
            },
          }}
        >
          {scheduleHeader}
          {schedulePanelContent}
        </Drawer>
        <Popover
          open={Boolean(infoAnchorEl)}
          anchorEl={infoAnchorEl}
          onClose={handleCloseInfo}
          anchorOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
          transformOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
          slotProps={epgInfoPopoverSlotProps}
        >
          <Typography variant="body2">{infoText}</Typography>
        </Popover>
      </>
    );
  }

  return (
    <>
      <ClickAwayListener onClickAway={handleClose}>
        <Box sx={{ position: "relative" }}>
          {button}

          <Popper
            open={open}
            anchorEl={scheduleAnchorEl}
            placement={epgSchedulePopperPlacement}
            disablePortal
            transition
            modifiers={epgSchedulePopperModifiers}
            sx={{
              zIndex: theme.zIndex.tooltip,
            }}
          >
            {({ TransitionProps, placement }) => (
              <Grow
                {...TransitionProps}
                timeout={140}
                style={{
                  transformOrigin: String(placement).startsWith("top")
                    ? "left bottom"
                    : "left top",
                }}
              >
                <Paper
                  sx={{
                    overflow: "hidden",
                    minWidth: 380,
                    maxWidth: "min(640px, calc(100vw - 16px))",
                    boxSizing: "border-box",
                    ...epgPanelSurfaceSx,
                  }}
                >
                  {scheduleHeader}
                  {schedulePanelContent}
                </Paper>
              </Grow>
            )}
          </Popper>
        </Box>
      </ClickAwayListener>
      <Popover
        open={Boolean(infoAnchorEl)}
        anchorEl={infoAnchorEl}
        onClose={handleCloseInfo}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        slotProps={epgInfoPopoverSlotProps}
      >
        <Typography variant="body2">{infoText}</Typography>
      </Popover>
    </>
  );
}

ChannelSchedule.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      title: PropTypes.string,
      subTitle: PropTypes.string,
      dateFrom: PropTypes.string,
      dateTo: PropTypes.string,
      description: PropTypes.string,
      categories: PropTypes.arrayOf(PropTypes.string),
      posterUrl: PropTypes.string,
      timezone: PropTypes.string,
    })
  ),
  loading: PropTypes.bool,
  hasPrevious: PropTypes.bool,
  hasNext: PropTypes.bool,
  onPrevious: PropTypes.func,
  onNext: PropTypes.func,
  timezone: PropTypes.string,
  channelVersionName: PropTypes.string,
  variant: PropTypes.oneOf(["outlined", "footer"]),
};
